<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useWhatsappEmbeddedSignup } from 'dashboard/composables/useWhatsappEmbeddedSignup';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import whatsappChannel from 'dashboard/api/channel/whatsappChannel';
import InboxHealthAPI from 'dashboard/api/inboxHealth';
import inboxMixin from 'shared/mixins/inboxMixin';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SettingsAccordion from 'dashboard/components-next/Settings/SettingsAccordion.vue';
import ImapSettings from '../ImapSettings.vue';
import SmtpSettings from '../SmtpSettings.vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TextArea from 'next/textarea/TextArea.vue';
import { sanitizeAllowedDomains } from 'dashboard/helper/URLHelper';
import WhatsappBusinessManagementToken from './WhatsappBusinessManagementToken.vue';

export default {
  components: {
    SettingsFieldSection,
    SettingsToggleSection,
    SettingsAccordion,
    ImapSettings,
    SmtpSettings,
    NextButton,
    TextArea,
    WhatsappBusinessManagementToken,
  },
  mixins: [inboxMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const { runEmbeddedSignup } = useWhatsappEmbeddedSignup();
    return { v$: useVuelidate(), runEmbeddedSignup };
  },
  data() {
    return {
      hmacMandatory: false,
      allowMobileWebview: false,
      whatsAppInboxAPIKey: '',
      isSyncingTemplates: false,
      allowedDomains: '',
      isUpdatingAllowedDomains: false,
      isSettingDefaults: false,
      expandedTemplates: {},
      isReconfiguring: false,
      wahaWebhookActive: false,
      wahaWebhookEvents: [],
      wahaWebhookUrl: '',
      isUpdatingWahaWebhook: false,
    };
  },
  validations: {
    whatsAppInboxAPIKey: { required },
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
    }),
    isEmbeddedSignupWhatsApp() {
      return this.inbox.provider_config?.source === 'embedded_signup';
    },
    showWhatsAppReconfigure() {
      return (
        this.isEmbeddedSignupWhatsApp &&
        this.isFeatureEnabledonAccount(
          this.accountId,
          this.isOnChatwootCloud
            ? FEATURE_FLAGS.WHATSAPP_EMBEDDED_SIGNUP_FLOW
            : FEATURE_FLAGS.WHATSAPP_RECONFIGURE
        )
      );
    },
    isForwardingEnabled() {
      return !!this.inbox.forwarding_enabled;
    },
    whatsappTemplates() {
      return this.inbox.message_templates || [];
    },
    approvedTemplates() {
      return this.whatsappTemplates.filter(t => t.status === 'APPROVED');
    },
    isWahaInbox() {
      return (
        this.isAPIInbox && this.inbox.inbox_identifier?.startsWith('waha_')
      );
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
    allowMobileWebview() {
      if (!this.isSettingDefaults) this.handleMobileWebviewFlag();
    },
    hmacMandatory() {
      if (!this.isSettingDefaults && this.isAWebWidgetInbox)
        this.handleHmacFlag();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      this.isSettingDefaults = true;
      this.hmacMandatory = this.inbox.hmac_mandatory || false;
      this.allowMobileWebview = (
        this.inbox.selected_feature_flags || []
      ).includes('allow_mobile_webview');
      this.allowedDomains = this.inbox.allowed_domains || '';
      const defaultWahaEvents = ['presence.update'];

      this.wahaWebhookActive = !!this.inbox.waha_webhook_active;
      this.wahaWebhookEvents =
        this.inbox.waha_webhook_events?.length > 0
          ? this.inbox.waha_webhook_events
          : defaultWahaEvents;
      this.wahaWebhookUrl =
        this.inbox.waha_webhook_url || this.inbox.callback_webhook_url || '';
      this.$nextTick(() => {
        this.isSettingDefaults = false;
      });
    },
    handleHmacFlag() {
      this.updateInbox();
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            hmac_mandatory: this.hmacMandatory,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async handleMobileWebviewFlag() {
      try {
        const currentFlags = this.inbox.selected_feature_flags || [];
        const selectedFlags = this.allowMobileWebview
          ? [...currentFlags, 'allow_mobile_webview']
          : currentFlags.filter(f => f !== 'allow_mobile_webview');

        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            selected_feature_flags: selectedFlags,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateAllowedDomains() {
      this.isUpdatingAllowedDomains = true;
      const sanitizedAllowedDomains = sanitizeAllowedDomains(
        this.allowedDomains
      );
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            allowed_domains: sanitizedAllowedDomains,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.allowedDomains = sanitizedAllowedDomains;
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isUpdatingAllowedDomains = false;
      }
    },
    async updateWahaWebhook(status = null) {
      if (!this.isWahaInbox) return;

      this.isUpdatingWahaWebhook = true;
      const active =
        status === 'active' || (status === null && this.wahaWebhookActive);
      const payloadStatus = active ? 'active' : 'inactive';

      try {
        const { data } = await InboxHealthAPI.updateWahaWebhook(this.inbox.id, {
          status: payloadStatus,
          events: this.wahaWebhookEvents,
        });
        this.wahaWebhookActive = data.waha_webhook_active || false;
        this.wahaWebhookEvents =
          data.waha_webhook_events || this.wahaWebhookEvents;
        this.wahaWebhookUrl = data.waha_webhook_url || this.wahaWebhookUrl;
        useAlert(this.$t('INBOX_MGMT.EDIT.WAHA_WEBHOOK.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(
          error.response?.data?.error ||
            this.$t('INBOX_MGMT.EDIT.WAHA_WEBHOOK.ERROR_MESSAGE')
        );
      } finally {
        this.isUpdatingWahaWebhook = false;
      }
    },
    async updateWhatsAppInboxAPIKey() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        payload.channel.provider_config = {
          ...this.inbox.provider_config,
          api_key: this.whatsAppInboxAPIKey,
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async reconfigureWhatsApp() {
      this.isReconfiguring = true;
      try {
        const credentials = await this.runEmbeddedSignup();
        // User dismissed the Meta popup without completing signup.
        if (!credentials) return;

        await whatsappChannel.reauthorizeWhatsApp({
          inboxId: this.inbox.id,
          ...credentials,
        });
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RECONFIGURE_SUCCESS')
        );
      } catch (error) {
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RECONFIGURE_ERROR')
        );
      } finally {
        this.isReconfiguring = false;
      }
    },
    async syncTemplates() {
      this.isSyncingTemplates = true;
      try {
        await this.$store.dispatch('inboxes/syncTemplates', this.inbox.id);
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUCCESS')
        );
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isSyncingTemplates = false;
      }
    },
  },
};
</script>

<template>
  <div v-if="isATwilioChannel">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.TITLE')"
      :help-text="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsFieldSection>
    <SettingsFieldSection
      v-if="isATwilioWhatsAppChannel"
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE')"
      :help-text="
        $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER')
      "
    >
      <NextButton :disabled="isSyncingTemplates" @click="syncTemplates">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON') }}
      </NextButton>
    </SettingsFieldSection>
  </div>

  <div v-else-if="isALineChannel">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.TITLE')"
      :help-text="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsFieldSection>
  </div>
  <div v-else-if="isAWebWidgetInbox">
    <div class="space-y-4">
      <SettingsToggleSection
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.TITLE')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.DESCRIPTION')
        "
        hide-toggle
      >
        <template #editor>
          <TextArea
            v-model="allowedDomains"
            :placeholder="
              $t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.PLACEHOLDER')
            "
            auto-height
            resize
            class="w-full [&>div]:!bg-transparent [&>div]:!border-none [&>div]:!border-0 [&>div]:px-0 [&>div]:pb-0 [&>div]:pt-0"
          />
          <div class="mt-3 flex justify-end">
            <NextButton
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
              :is-loading="isUpdatingAllowedDomains"
              @click="updateAllowedDomains"
            />
          </div>
        </template>
      </SettingsToggleSection>
      <SettingsToggleSection
        v-model="allowMobileWebview"
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.ALLOW_MOBILE_WEBVIEW.LABEL')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.ALLOW_MOBILE_WEBVIEW.SUBTITLE')
        "
      />
    </div>

    <SettingsAccordion
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.TITLE')"
      class="mt-6"
    >
      <SettingsToggleSection
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.TITLE')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.DESCRIPTION')
        "
        hide-toggle
      >
        <template #editor>
          <p class="mb-1 text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.SECRET_KEY') }}
          </p>
          <woot-code :script="inbox.hmac_token" />
          <p class="mt-1.5 text-label-small text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION') }}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.chatwoot.com/docs/product/channels/live-chat/sdk/identity-validation/"
              class="text-n-blue-11 hover:underline text-label-small"
            >
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.VIEW_DOCS')
              }}
            </a>
          </p>
        </template>
      </SettingsToggleSection>

      <SettingsToggleSection
        v-model="hmacMandatory"
        :header="
          $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.REQUIRE_LABEL')
        "
        :description="
          $t(
            'INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.REQUIRE_DESCRIPTION'
          )
        "
      />
    </SettingsAccordion>
  </div>
  <div v-else-if="isAPIInbox">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER_SUB_TEXT')"
    >
      <woot-code :script="inbox.inbox_identifier" />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION')"
    >
      <woot-code :script="inbox.hmac_token" />
    </SettingsFieldSection>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION')"
    >
      <div class="flex gap-2 items-center">
        <input
          id="hmacMandatory"
          v-model="hmacMandatory"
          type="checkbox"
          @change="handleHmacFlag"
        />
        <label for="hmacMandatory" class="text-body-main text-n-slate-12">
          {{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}
        </label>
      </div>
    </SettingsFieldSection>

    <template v-if="isWahaInbox">
      <SettingsAccordion
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WAHA_WEBHOOK.TITLE')"
        class="mt-6"
      >
        <div class="space-y-4">
          <p class="text-body-para text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WAHA_WEBHOOK.ACTIVE_SUBTITLE') }}
          </p>

          <div class="flex flex-col gap-1">
            <span class="text-label-small text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WAHA_WEBHOOK.EVENTS_LABEL') }}
            </span>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="event in wahaWebhookEvents"
                :key="event"
                class="px-2 py-1 text-xs font-medium rounded-md bg-n-slate-3 text-n-slate-11"
              >
                {{ event }}
              </span>
            </div>
          </div>

          <div class="flex flex-col gap-1">
            <span class="text-label-small text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WAHA_WEBHOOK.URL_LABEL') }}
            </span>
            <code class="text-xs break-all">{{ wahaWebhookUrl || '-' }}</code>
          </div>

          <div class="flex gap-2 mt-4">
            <NextButton
              :is-loading="isUpdatingWahaWebhook"
              :disabled="isUpdatingWahaWebhook || wahaWebhookActive"
              @click="updateWahaWebhook('active')"
            >
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WAHA_WEBHOOK.ACTIVATE_BUTTON') }}
            </NextButton>
            <NextButton
              :is-loading="isUpdatingWahaWebhook"
              :disabled="isUpdatingWahaWebhook || !wahaWebhookActive"
              variant="secondary"
              @click="updateWahaWebhook('inactive')"
            >
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WAHA_WEBHOOK.PAUSE_BUTTON') }}
            </NextButton>
          </div>
        </div>
      </SettingsAccordion>
    </template>
  </div>
  <div v-else-if="isAnEmailChannel">
    <div>
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_TITLE')"
        :help-text="
          isForwardingEnabled
            ? $t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_SUB_TEXT')
            : ''
        "
      >
        <woot-code
          v-if="isForwardingEnabled"
          :script="inbox.forward_to_email"
        />
        <div
          v-else
          class="py-2 px-3 bg-n-amber-3 outline-n-amber-4 text-n-amber-11 outline outline-1 -outline-offset-1 rounded-xl"
        >
          <p class="text-body-para mb-0">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_NOT_CONFIGURED') }}
          </p>
        </div>
      </SettingsFieldSection>
    </div>
    <ImapSettings :inbox="inbox" />
    <SmtpSettings v-if="inbox.imap_enabled" :inbox="inbox" />
  </div>
  <div v-else-if="isAWhatsAppChannel && !isATwilioChannel">
    <div v-if="inbox.provider_config">
      <!-- Embedded Signup Section -->
      <template v-if="isEmbeddedSignupWhatsApp">
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_SUBHEADER')
          "
        >
          <woot-code :script="inbox.provider_config.webhook_verify_token" />
        </SettingsFieldSection>
        <SettingsFieldSection
          v-if="showWhatsAppReconfigure"
          :label="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_TITLE')
          "
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_DESCRIPTION')
          "
        >
          <NextButton
            :is-loading="isReconfiguring"
            :disabled="isReconfiguring"
            @click="reconfigureWhatsApp"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RECONFIGURE_BUTTON') }}
          </NextButton>
        </SettingsFieldSection>
      </template>

      <!-- Manual Setup Section -->
      <template v-else-if="!isEmbeddedSignupWhatsApp">
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_SUBHEADER')
          "
        >
          <woot-code :script="inbox.provider_config.webhook_verify_token" />
        </SettingsFieldSection>
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_SUBHEADER')
          "
        >
          <woot-code :script="inbox.provider_config.api_key" />
        </SettingsFieldSection>
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_SUBHEADER')
          "
        >
          <div
            class="flex flex-1 justify-between items-center whatsapp-settings--content"
          >
            <woot-input
              v-model="whatsAppInboxAPIKey"
              type="text"
              class="flex-1 mr-2 [&>input]:!mb-0"
              :placeholder="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_PLACEHOLDER'
                )
              "
            />
            <NextButton
              :disabled="v$.whatsAppInboxAPIKey.$invalid"
              @click="updateWhatsAppInboxAPIKey"
            >
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON')
              }}
            </NextButton>
          </div>
        </SettingsFieldSection>
      </template>
      <WhatsappBusinessManagementToken
        v-if="
          isOnChatwootCloud &&
          inbox.provider === 'whatsapp_cloud' &&
          isEmbeddedSignupWhatsApp
        "
        :inbox="inbox"
      />
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE')"
        :help-text="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER')
        "
      >
        <NextButton :disabled="isSyncingTemplates" @click="syncTemplates">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON') }}
        </NextButton>
      </SettingsFieldSection>

      <SettingsFieldSection
        v-if="approvedTemplates.length > 0"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_LIST_TITLE')"
        :help-text="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_LIST_SUBTITLE')
        "
      >
        <div class="space-y-2">
          <div
            v-for="template in approvedTemplates"
            :key="template.name"
            class="border border-n-slate-4 rounded-lg bg-n-slate-1 overflow-hidden"
          >
            <button
              type="button"
              class="w-full px-4 py-3 flex justify-between items-center hover:bg-n-slate-2 transition-colors"
              @click="
                expandedTemplates[template.name] =
                  !expandedTemplates[template.name]
              "
            >
              <div class="flex-1 text-left">
                <p class="font-medium text-n-slate-12">{{ template.name }}</p>
                <p class="text-xs text-n-slate-10 mt-0.5">
                  {{ template.category || 'General' }}
                </p>
                <p
                  v-if="template.body || template.text"
                  class="text-xs text-n-slate-11 mt-1 line-clamp-2"
                >
                  {{ template.body || template.text }}
                </p>
              </div>
              <div class="flex items-center gap-2">
                <span
                  class="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded"
                >
                  {{ template.status }}
                </span>
                <svg
                  class="w-5 h-5 text-n-slate-11 transition-transform"
                  :class="[
                    expandedTemplates[template.name] ? 'rotate-180' : '',
                  ]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 14l-7 7m0 0l-7-7m7 7V3"
                  />
                </svg>
              </div>
            </button>

            <div
              v-if="expandedTemplates[template.name]"
              class="px-4 py-3 border-t border-n-slate-4 bg-n-slate-2 space-y-4"
            >
              <div class="space-y-3">
                <div v-if="template.components">
                  <p class="text-xs font-medium text-n-slate-11 mb-2">
                    {{ $t('INBOX_MGMT.TEMPLATE_COMPLETE_DATA') }}
                  </p>
                  <div class="space-y-3 bg-n-slate-1 p-3 rounded">
                    <div
                      v-for="(component, idx) in template.components"
                      :key="idx"
                      class="border-b border-n-slate-3 pb-3 last:border-b-0 last:pb-0"
                    >
                      <div v-if="component.type === 'HEADER'" class="space-y-1">
                        <p class="text-xs font-semibold text-n-slate-12">
                          {{ $t('INBOX_MGMT.TEMPLATE_PREVIEW.HEADER') }}
                        </p>
                        <p class="text-sm text-n-slate-12 whitespace-pre-wrap">
                          {{ component.text }}
                        </p>
                      </div>
                      <div
                        v-else-if="component.type === 'BODY'"
                        class="space-y-1"
                      >
                        <p class="text-xs font-semibold text-n-slate-12">
                          {{ $t('INBOX_MGMT.TEMPLATE_PREVIEW.BODY') }}
                        </p>
                        <p class="text-sm text-n-slate-12 whitespace-pre-wrap">
                          {{ component.text }}
                        </p>
                      </div>
                      <div
                        v-else-if="component.type === 'FOOTER'"
                        class="space-y-1"
                      >
                        <p class="text-xs font-semibold text-n-slate-12">
                          {{ $t('INBOX_MGMT.TEMPLATE_PREVIEW.FOOTER') }}
                        </p>
                        <p class="text-sm text-n-slate-12">
                          {{ component.text }}
                        </p>
                      </div>
                      <div
                        v-else-if="component.type === 'BUTTONS'"
                        class="space-y-1"
                      >
                        <p class="text-xs font-semibold text-n-slate-12">
                          {{ $t('INBOX_MGMT.TEMPLATE_PREVIEW.BUTTONS') }}
                        </p>
                        <div class="space-y-2">
                          <div
                            v-for="(btn, bidx) in component.buttons"
                            :key="bidx"
                            class="bg-n-blue-2 p-2 rounded text-sm"
                          >
                            <p class="font-medium text-n-blue-12">
                              {{ btn.text }}
                            </p>
                            <p
                              v-if="btn.url"
                              class="text-xs text-n-blue-11 break-all"
                            >
                              {{ btn.url }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="template.status" class="flex items-center gap-2">
                  <p class="text-xs font-medium text-n-slate-11">
                    {{ $t('INBOX_MGMT.STATUS') }}:
                  </p>
                  <span
                    v-if="template.status === 'APPROVED'"
                    class="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded"
                    >✅ {{ template.status }}</span
                  >
                  <span
                    v-else-if="template.status === 'PENDING'"
                    class="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded"
                    >{{ template.status }}</span
                  >
                  <span
                    v-else
                    class="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded"
                    >❌ {{ template.status }}</span
                  >
                </div>

                <div v-if="template.category" class="flex items-center gap-2">
                  <p class="text-xs font-medium text-n-slate-11">
                    {{ $t('INBOX_MGMT.TEMPLATE_PREVIEW.CATEGORY') }}
                  </p>
                  <span
                    class="px-2 py-1 bg-n-slate-3 text-n-slate-12 text-xs rounded"
                    >{{ template.category }}</span
                  >
                </div>

                <div v-if="template.language" class="flex items-center gap-2">
                  <p class="text-xs font-medium text-n-slate-11">
                    {{ $t('INBOX_MGMT.TEMPLATE_PREVIEW.LANGUAGE') }}
                  </p>
                  <span
                    class="px-2 py-1 bg-n-slate-3 text-n-slate-12 text-xs rounded"
                    >{{ template.language }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </SettingsFieldSection>

      <SettingsFieldSection
        v-else
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_LIST_TITLE')"
        :help-text="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_LIST_SUBTITLE')
        "
      >
        <div class="py-4 px-3 bg-n-slate-2 rounded-lg text-center">
          <p class="text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_EMPTY') }}
          </p>
        </div>
      </SettingsFieldSection>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.whatsapp-settings--content {
  :deep(input) {
    margin-bottom: 0;
  }
}
</style>
